// src/controllers/DietaBalanceadaController.js
import { useEffect, useMemo, useState } from "react";
import {
    buildInfoGeneral,
    buildMateriasSeleccionadas,
    buildDetallesBromatologicos,
    DETALLE_KEYS,
} from "../models/dietaBalanceada.model";

import { cargarDetalleMateria } from "./MateriasController";

/**
 * Resuelve la fuente de datos del Paso 1 (Datos del animal):
 * 1) Prop recibida,
 * 2) DietasController.getDatosAnimal(),
 * 3) localStorage("np_datosAnimal")
 */
export function resolveDatosAnimal(DietasController, datosAnimalProp) {
    if (datosAnimalProp) return datosAnimalProp;

    if (typeof DietasController?.getDatosAnimal === "function") {
        const fromCtl = DietasController.getDatosAnimal();
        if (fromCtl) return fromCtl;
    }

    try {
        const ls = localStorage.getItem("np_datosAnimal");
        if (ls) return JSON.parse(ls);
    } catch (_) { }

    return {};
}

/**
 * Hook controlador para DietaBalanceada:
 * - Información general
 * - Listado con detalles bromatológicos por materia (Banco y Temporales)
 * - Carga de restricciones del paso 4 (costo, minKg, maxKg)
 */
export function useDietaBalanceadaController(DietasController, datosAnimalProp) {
    const [raw, setRaw] = useState(null);

    const [seleccionadas, setSeleccionadas] = useState([]);
    const [restricciones, setRestricciones] = useState({});

    const [detallesById, setDetallesById] = useState({});

    // Carga inicial
    useEffect(() => {
        const data = resolveDatosAnimal(DietasController, datosAnimalProp);
        setRaw(data || {});

        const sel =
            typeof DietasController?.getSeleccionadas === "function"
                ? DietasController.getSeleccionadas()
                : JSON.parse(localStorage.getItem("np_sel") || "[]");

        // Preferimos lo que esté en el controlador; si no, localStorage
        let restr =
            typeof DietasController?.getRestricciones === "function"
                ? DietasController.getRestricciones()
                : {};

        if (!restr || typeof restr !== "object" || !Object.keys(restr).length) {
            try {
                const rawRestr = JSON.parse(localStorage.getItem("np_restriccionMateria") || "{}");
                restr = rawRestr.byId || JSON.parse(localStorage.getItem("np_restr") || "{}") || {};
            } catch { restr = {}; }
        }

        setSeleccionadas(Array.isArray(sel) ? sel : []);
        setRestricciones(restr && typeof restr === "object" ? restr : {});
    }, [DietasController, datosAnimalProp]);

    // Cargar detalles
    useEffect(() => {
        let cancel = false;

        // detalles de temporales guardados por Paso 3 en localStorage
        let tempDetallesLS = {};
        try {
            tempDetallesLS = JSON.parse(localStorage.getItem("np_temp_detalles") || "{}");
        } catch (_) { }

        async function fetchDetalles() {
            const acc = {};
            for (const m of seleccionadas || []) {
                try {
                    // ---- Materia temporal: buscar inline -> DietasController -> localStorage
                    if (m.temporal || m.isTemporal || m.esTemporal) {
                        const inline =
                            m.detalle || m.bromatologia || m.detalles || m.composicion || null;

                        const fromCtl =
                            (typeof DietasController?.getDetalleMateria === "function")
                                ? DietasController.getDetalleMateria(m.id)
                                : null;

                        const fromLS = tempDetallesLS?.[m.id] || null;

                        acc[m.id] = inline || fromCtl || fromLS || {};
                        continue;
                    }

                    // ---- Materia de banco: cargar desde API / controlador
                    let det = await cargarDetalleMateria(m.id);
                    if (Array.isArray(det)) det = det[0];
                    acc[m.id] = det || {};
                } catch (e) {
                    acc[m.id] = {};
                }
            }
            if (!cancel) setDetallesById(acc);
        }

        if (seleccionadas && seleccionadas.length) fetchDetalles();
        else setDetallesById({});

        return () => { cancel = true; };
    }, [seleccionadas, DietasController]);

    const infoGeneral = useMemo(() => buildInfoGeneral(raw), [raw]);

    // buildMateriasSeleccionadas puede mostrar costo/minKg/maxKg si decide
    const materias = useMemo(
        () => buildMateriasSeleccionadas(seleccionadas, restricciones),
        [seleccionadas, restricciones]
    );

    const detalleKeys = DETALLE_KEYS;

    const materiasConDetalles = useMemo(
        () => buildDetallesBromatologicos(seleccionadas, detallesById, detalleKeys),
        [seleccionadas, detallesById, detalleKeys]
    );

    return { infoGeneral, materias, materiasConDetalles, detalleKeys };
}
